<script setup lang="ts">
// Nœud récursif de l'arbre des taxons (web + mobile). `selected` est l'ensemble étendu des FEUILLES
// cochées (fourni par AppTaxonTree) : l'état d'un nœud se déduit de ses feuilles, le toggle émet
// toujours des ids de feuilles, la compaction parent/enfant est faite par AppTaxonTree.
import type { SpecieTaxonTreeResource } from "@carnet/api/backoffice";

const props = withDefaults(
  defineProps<{
    node: SpecieTaxonTreeResource;
    selected: Set<string>;
    defaultOpen: Set<string>;
    depth?: number;
  }>(),
  { depth: 0 }
);

const emit = defineEmits<{
  toggle: [ids: string[], checked: boolean];
}>();

const { t } = useI18n();

const open = ref(props.defaultOpen.has(props.node.id));

const childrenListId = computed(() => `taxon-children-${props.node.id}`);

function collectLeafIds(node: SpecieTaxonTreeResource): string[] {
  if (node.children.length === 0) return [node.id];
  const out: string[] = [];
  for (const child of node.children) out.push(...collectLeafIds(child));
  return out;
}

const leafIds = computed(() => collectLeafIds(props.node));
const hasChildren = computed(() => props.node.children.length > 0);

const state = computed<"unchecked" | "indeterminate" | "checked">(() => {
  const ids = leafIds.value;
  if (!ids.length) return "unchecked";
  let count = 0;
  for (const id of ids) if (props.selected.has(id)) count++;
  if (count === 0) return "unchecked";
  if (count === ids.length) return "checked";
  return "indeterminate";
});

const checkboxRef = useTemplateRef<HTMLInputElement>("checkboxRef");

watchEffect(() => {
  if (checkboxRef.value) checkboxRef.value.indeterminate = state.value === "indeterminate";
});

function onChange() {
  emit("toggle", leafIds.value, state.value !== "checked");
}

function onChildToggle(ids: string[], checked: boolean) {
  emit("toggle", ids, checked);
}
</script>

<template>
  <li>
    <div class="flex items-start gap-3 py-2" :style="{ paddingInlineStart: `${depth * 1.5}rem` }">
      <input
        :id="`taxon-${node.id}`"
        ref="checkboxRef"
        type="checkbox"
        class="accent-blue-700 size-4 mt-px rounded-xs"
        :checked="state === 'checked'"
        @change="onChange"
      />
      <label :for="`taxon-${node.id}`" class="grow cursor-pointer select-none">{{ node.name }}</label>
      <UButton
        v-if="hasChildren"
        variant="ghost"
        :aria-expanded="open"
        :aria-controls="childrenListId"
        :aria-label="t('taxons.toggleChildren', { name: node.name })"
        class="p-2 -my-2 -mr-0.5"
        @click="() => { open = !open }"
      >
        <UIcon :name="open ? 'icon:chevronUp' : 'icon:chevronDown'" class="size-4" />
      </UButton>
    </div>
    <ul v-if="hasChildren && open" :id="childrenListId" role="group" class="flex flex-col">
      <AppTaxonNode v-for="child in node.children" :key="child.id" :node="child" :selected="selected" :default-open="defaultOpen" :depth="depth + 1" @toggle="onChildToggle" />
    </ul>
  </li>
</template>
